import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, Star, ShieldCheck, Car, Sparkles, Clock, MessageCircle, Quote, ArrowRight } from "lucide-react";
import { ISACK_AVATAR, FLEET_PRADO, SPICE_VANILLA, SPICE_CINNAMON, STONE_CATHEDRAL } from "@/lib/media";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: "easeOut" } }),
};

const TRUST_STATS = [
  { icon: Star, value: "4.9/5", label: "Guest rating" },
  { icon: ShieldCheck, value: "Licensed", label: "ZATO registered operator" },
  { icon: Car, value: "Private fleet", label: "Prado · Minibus · Coaster" },
  { icon: Clock, value: "24/7", label: "Isack always answers" },
];

const PROMISES = [
  { icon: MessageCircle, title: "One conversation, everything arranged", text: "No forms, no browsing. Isack listens, understands, and builds your whole trip in a single warm chat." },
  { icon: Sparkles, title: "Handpicked, honest pricing", text: "Every price comes straight from our real rate book — service, bottled water and fees included. No surprises." },
  { icon: ShieldCheck, title: "Local, trusted, on the ground", text: "A Zanzibar-based team with our own drivers and guides. From airport pickup to sunset, you are cared for." },
];

const GALLERY = [
  { img: STONE_CATHEDRAL, label: "Stone Town heritage" },
  { img: SPICE_VANILLA, label: "Spice farm — fresh vanilla" },
  { img: SPICE_CINNAMON, label: "Cinnamon, straight from the bark" },
];

const REVIEWS = [
  { name: "Sophie & Daniel", from: "London, UK", text: "Isack planned our honeymoon better than any human agent we've used. A private sunset dhow, spice farm, and the smoothest airport pickup. Felt like a friend on the island.", tour: "Honeymoon · 6 days" },
  { name: "The Andersons", from: "Oslo, Norway", text: "Travelling with two kids, we were nervous. Isack shaped everything around them — gentle pacing, Safari Blue, dolphins. The Prado was spotless and on time.", tour: "Family · 5 days" },
  { name: "Marco R.", from: "Milan, Italy", text: "Needed a fast airport transfer at midnight. One message, exact price, done. Then he talked me into Mnemba snorkelling — best decision of the trip.", tour: "Transfer + Mnemba" },
  { name: "Aisha K.", from: "Dubai, UAE", text: "Elegant, warm and incredibly quick. It genuinely feels five-star from the very first message. Stone Town tour with Isack's guide was unforgettable.", tour: "Stone Town + Spice" },
];

export const Home = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");

  const startWithIsack = (text) => {
    navigate("/concierge", { state: { initialMessage: (text || message).trim() || undefined } });
  };

  return (
    <div className="sadara-bg grain min-h-screen w-full overflow-x-hidden text-foreground" data-testid="home-page">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-5 md:px-10 py-4 backdrop-blur-md bg-background/40 border-b border-gold/10">
        <div>
          <h1 className="font-display text-xl md:text-2xl tracking-wide leading-none" data-testid="brand-name">
            Sadara <span className="gold-text">Tour</span> Zanzibar
          </h1>
          <p className="text-[9px] md:text-[10px] uppercase tracking-[0.35em] text-gold/60 mt-1">Five-Star Private Concierge</p>
        </div>
        <button onClick={() => startWithIsack("")} className="rounded-full bg-gold px-5 py-2.5 text-xs md:text-sm font-semibold text-background hover:bg-gold-soft transition-colors" data-testid="nav-meet-isack">
          Meet Isack
        </button>
      </header>

      {/* ===== HERO — Isack is the heart ===== */}
      <section className="relative min-h-screen flex items-center justify-center px-5 pt-28 pb-16" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full bg-gold/10 blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-3xl w-full flex flex-col items-center text-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="relative mb-7">
            <div className="absolute -inset-4 rounded-full bg-gold/25 blur-2xl animate-sun-pulse" aria-hidden="true" />
            <img src={ISACK_AVATAR} alt="Isack, your private concierge" className="relative w-28 h-28 md:w-32 md:h-32 rounded-full object-cover border-2 border-gold/60 gold-glow" data-testid="hero-isack-avatar" />
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] uppercase tracking-[0.3em] text-gold/80 bg-background/70 rounded-full px-3 py-1 border border-gold/20">Isack · Online</span>
          </motion.div>

          <motion.p initial="hidden" animate="show" custom={1} variants={fadeUp} className="text-xs uppercase tracking-[0.35em] text-gold/70 mb-4">
            Meet your concierge
          </motion.p>
          <motion.h2 initial="hidden" animate="show" custom={2} variants={fadeUp} className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]">
            Zanzibar, arranged<br />entirely by <span className="gold-text italic">Isack</span>.
          </motion.h2>
          <motion.p initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-5 text-base md:text-lg text-muted-foreground max-w-xl">
            No menus. No forms. Just tell Isack what you're dreaming of — a honeymoon, a family escape, or a quick airport transfer — and he'll craft it, price it, and book it, all in one warm conversation.
          </motion.p>

          <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp} className="mt-8 w-full max-w-xl">
            <div className="flex items-center gap-2 rounded-full glass border border-gold/30 pl-5 pr-1.5 py-1.5">
              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && startWithIsack()}
                placeholder="e.g. Something romantic for our honeymoon in Paje…"
                className="flex-1 bg-transparent outline-none text-sm md:text-base placeholder:text-muted-foreground/70"
                data-testid="hero-chat-input"
              />
              <button onClick={() => startWithIsack()} className="shrink-0 h-11 px-5 rounded-full bg-gold text-background flex items-center gap-2 text-sm font-semibold hover:bg-gold-soft transition-colors" data-testid="hero-start-btn">
                Start <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {["Airport transfer", "Honeymoon escape", "Family, 5 days", "Spice & Stone Town"].map((c) => (
                <button key={c} onClick={() => startWithIsack(c === "Airport transfer" ? "I'd like a quick airport transfer, please." : `I'm interested in a ${c.toLowerCase()}.`)}
                  className="rounded-full border border-gold/25 bg-white/5 px-3.5 py-1.5 text-xs text-gold/90 hover:bg-gold/10 transition-colors" data-testid={`hero-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {c}
                </button>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-gold/40">Scroll to explore</div>
      </section>

      {/* ===== SECTION 1 — TRUST ===== */}
      <section className="relative py-24 px-5 md:px-10 border-t border-gold/10" data-testid="trust-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.35em] text-gold/70">Why travellers trust us</p>
            <h3 className="font-display text-3xl md:text-4xl font-light mt-3">A five-star standard, held on the ground in Zanzibar.</h3>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {TRUST_STATS.map((s, i) => (
              <motion.div key={s.label} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="glass rounded-2xl p-5" data-testid={`trust-stat-${i}`}>
                <s.icon className="w-6 h-6 text-gold mb-3" />
                <p className="font-display text-2xl">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-12 items-stretch">
            {/* Fleet showcase — real Prado */}
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative rounded-3xl overflow-hidden min-h-[320px] group" data-testid="fleet-showcase">
              <img src={FLEET_PRADO} alt="Sadara private fleet — Land Cruiser Prado in Stone Town" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ocean via-ocean/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Our own fleet</p>
                <p className="font-display text-2xl mt-1">Arrive in a private Prado</p>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">Air-conditioned, spotless, driven by our own trusted team — from the airport to every corner of the island.</p>
              </div>
            </motion.div>

            {/* Promises */}
            <div className="flex flex-col gap-4">
              {PROMISES.map((p, i) => (
                <motion.div key={p.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                  className="glass rounded-2xl p-5 flex gap-4" data-testid={`promise-${i}`}>
                  <div className="w-11 h-11 shrink-0 rounded-full bg-gold/15 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="font-display text-lg leading-tight">{p.title}</p>
                    <p className="text-sm text-muted-foreground mt-1">{p.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Real gallery strip */}
          <div className="grid grid-cols-3 gap-3 md:gap-4 mt-6">
            {GALLERY.map((g, i) => (
              <motion.div key={g.label} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="relative rounded-2xl overflow-hidden h-32 md:h-44 group" data-testid={`gallery-${i}`}>
                <img src={g.img} alt={g.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean/80 to-transparent" />
                <p className="absolute bottom-3 left-3 text-xs text-gold/90">{g.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 2 — REVIEWS ===== */}
      <section className="relative py-24 px-5 md:px-10 border-t border-gold/10" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.35em] text-gold/70">Loved by our guests</p>
            <h3 className="font-display text-3xl md:text-4xl font-light mt-3">Real journeys, arranged by Isack.</h3>
            <div className="flex items-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" />)}
              <span className="ml-2 text-sm text-muted-foreground">4.9 average from 2,000+ guests</span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {REVIEWS.map((r, i) => (
              <motion.div key={r.name} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="glass rounded-3xl p-6 relative" data-testid={`review-${i}`}>
                <Quote className="w-8 h-8 text-gold/30 absolute top-5 right-5" />
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-gold text-gold" />)}
                </div>
                <p className="text-sm md:text-base text-foreground/90 leading-relaxed">"{r.text}"</p>
                <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
                  <div>
                    <p className="font-display text-lg leading-tight">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.from}</p>
                  </div>
                  <span className="text-[11px] text-gold/80 border border-gold/25 rounded-full px-3 py-1">{r.tour}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOOTER CTA — back to the heart: Isack ===== */}
      <section className="relative py-24 px-5 border-t border-gold/10 text-center" data-testid="cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full bg-gold/10 blur-[120px]" aria-hidden="true" />
        <div className="relative z-10 max-w-2xl mx-auto">
          <img src={ISACK_AVATAR} alt="Isack" className="w-20 h-20 rounded-full object-cover border-2 border-gold/60 mx-auto mb-6 gold-glow" />
          <h3 className="font-display text-3xl md:text-4xl font-light">Your concierge is waiting.</h3>
          <p className="mt-4 text-muted-foreground">Tell Isack one thing you're dreaming of. He'll take care of the rest.</p>
          <button onClick={() => startWithIsack("")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-background hover:bg-gold-soft transition-colors" data-testid="cta-start-btn">
            Start with Isack <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <p className="relative z-10 mt-16 text-xs text-muted-foreground">© {new Date().getFullYear()} Sadara Tour Zanzibar · Island Express Safaris & Tours</p>
      </section>
    </div>
  );
};
